import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Button } from '@vaiu/ui/button';
import { Input } from '@vaiu/ui/input';
import { Badge } from '@vaiu/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@vaiu/ui/dialog';
import { Textarea } from '@vaiu/ui/textarea';
import {
  ArrowLeft,
  Send,
  Terminal,
  Sparkles,
  User,
  Bot,
  Wallet,
  QrCode,
  Home,
  Clock,
} from 'lucide-react';
import { COMMAND_TEMPLATES, type ShellExecutor } from '@vaiu/shell';

interface ToastFunction {
  (options: {
    title: string;
    description?: string;
    variant?: 'default' | 'destructive';
  }): void;
}

interface Message {
  id: string;
  text: string;
  type: 'user' | 'system';
  timestamp: Date;
  action?: string;
  data?: any;
  executionTime?: number;
  isProcessing?: boolean;
}

interface VaiuShellProps {
  /** Executes commands via the same REST APIs as the app UI (not platform /cli). */
  shellExecutor: ShellExecutor;
  toast: ToastFunction;
  /** When false, hide create-token templates and shortcuts (developer app). */
  allowTokenCreation?: boolean;
}

const VaiuShell: React.FC<VaiuShellProps> = ({
  shellExecutor,
  toast,
  allowTokenCreation = true,
}) => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [commandHistory, setCommandHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [selectedSuggestion, setSelectedSuggestion] = useState(-1);
  const [scriptDialogOpen, setScriptDialogOpen] = useState(false);
  const [scriptContent, setScriptContent] = useState('');
  const [scriptFileName, setScriptFileName] = useState('');
  const [isUploadingScript, setIsUploadingScript] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const commandTemplates = allowTokenCreation
    ? COMMAND_TEMPLATES
    : COMMAND_TEMPLATES.filter((t) => !/create /i.test(t));

  useEffect(() => {
    // Scroll to top when component mounts
    scrollToTop();
    
    // Welcome message
    addSystemMessage(
      allowTokenCreation
        ? "Welcome to Vaiu Shell! 🚀\n\nI use the same APIs as vaiu.app — not the platform CLI. Try:\n• \"create $50 token\" or \"make 25 EUR payment\"\n• \"redeem ABC123\" or \"use token XYZ789\"\n• \"show balances\" or \"balance USD\"\n• \"open transactions\" or \"search merchants coffee\"\n• \"help\" for all commands\n\nWhat would you like to do?"
        : "Welcome to Vaiu Shell! 🚀\n\nThis developer shell does not generate cash or invoice tokens. Create those on vaiu.app. Try:\n• \"show balances\" or \"balance USD\"\n• \"redeem ABC123\" or \"token info ABC123\"\n• \"open transactions\"\n• \"help\" for all commands\n\nWhat would you like to do?"
    );
    
    // Focus input
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Update suggestions based on input
  useEffect(() => {
    if (input.trim()) {
      const inputLower = input.toLowerCase();
      const filteredSuggestions = commandTemplates.filter(template =>
        template.toLowerCase().startsWith(inputLower) && template !== input
      );
      setSuggestions(filteredSuggestions.slice(0, 5));
      setSelectedSuggestion(-1);
    } else {
      setSuggestions([]);
      setSelectedSuggestion(-1);
    }
  }, [input]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const addMessage = (text: string, type: 'user' | 'system', action?: string, data?: any, executionTime?: number, isProcessing?: boolean) => {
    const newMessage: Message = {
      id: Date.now().toString(),
      text,
      type,
      timestamp: new Date(),
      action,
      data,
      executionTime,
      isProcessing
    };
    setMessages(prev => [...prev, newMessage]);
    return newMessage;
  };

  const addSystemMessage = (text: string, action?: string, data?: any, executionTime?: number) => {
    return addMessage(text, 'system', action, data, executionTime);
  };

  const addUserMessage = (text: string) => {
    return addMessage(text, 'user');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isProcessing) return;

    const userInput = input.trim();
    setInput('');
    setIsProcessing(true);

    setCommandHistory((prev) => [...prev.slice(-49), userInput]);
    setHistoryIndex(-1);
    addUserMessage(userInput);

    try {
      const result = await shellExecutor.execute(userInput);
      addSystemMessage(
        result.message,
        result.action,
        result.data,
        result.executionTime
      );
      if (result.action === 'navigate' && typeof result.data?.path === 'string') {
        navigate(result.data.path);
      } else if (
        (result.action === 'create-token-ui' || result.action === 'redeem-token-ui') &&
        typeof result.data?.path === 'string'
      ) {
        navigate(result.data.path);
      }
    } catch (error) {
      console.error('Command processing error:', error);
      addSystemMessage(
        `❌ ${error instanceof Error ? error.message : 'Something went wrong. Please try again.'}`
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Handle suggestions navigation
    if (suggestions.length > 0) {
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedSuggestion(prev => prev <= 0 ? suggestions.length - 1 : prev - 1);
        return;
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedSuggestion(prev => prev >= suggestions.length - 1 ? 0 : prev + 1);
        return;
      } else if (e.key === 'Tab' && selectedSuggestion >= 0) {
        e.preventDefault();
        setInput(suggestions[selectedSuggestion]);
        setSuggestions([]);
        setSelectedSuggestion(-1);
        return;
      } else if (e.key === 'Enter' && selectedSuggestion >= 0) {
        e.preventDefault();
        setInput(suggestions[selectedSuggestion]);
        setSuggestions([]);
        setSelectedSuggestion(-1);
        return;
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setSuggestions([]);
        setSelectedSuggestion(-1);
        return;
      }
    }

    // Handle command history navigation (only when no suggestions)
    if (suggestions.length === 0) {
      if (e.key === 'ArrowUp' && commandHistory.length > 0) {
        e.preventDefault();
        const newIndex = historyIndex === -1 ? commandHistory.length - 1 : Math.max(0, historyIndex - 1);
        setHistoryIndex(newIndex);
        setInput(commandHistory[newIndex]);
      } else if (e.key === 'ArrowDown' && historyIndex >= 0) {
        e.preventDefault();
        const newIndex = historyIndex === commandHistory.length - 1 ? -1 : historyIndex + 1;
        setHistoryIndex(newIndex);
        setInput(newIndex === -1 ? '' : commandHistory[newIndex]);
      }
    }

    // Handle tab completion
    if (e.key === 'Tab' && suggestions.length > 0 && selectedSuggestion === -1) {
      e.preventDefault();
      setInput(suggestions[0]);
      setSuggestions([]);
    }
  };

  const handleScriptUpload = async () => {
    if (!scriptContent.trim()) {
      toast({
        title: "Script Required",
        description: "Please enter script content to upload.",
        variant: "destructive",
      });
      return;
    }

    setIsUploadingScript(true);

    try {
      const fileName = scriptFileName.trim() || 'script.vaiu';
      const result = await shellExecutor.executeScript(scriptContent.trim());

      setScriptContent('');
      setScriptFileName('');
      setScriptDialogOpen(false);

      addSystemMessage(
        `✅ Script executed!\n\n` +
          `📄 File: ${fileName}\n` +
          `📊 Commands: ${result.totalCommands}\n` +
          `✅ Success: ${result.successCount}\n` +
          `❌ Failed: ${result.failureCount}`
      );

      if (result.failureCount > 0) {
        const failedCommands = result.results
          .filter((r) => !r.success)
          .map((r) => `Line ${r.lineNumber}: ${r.command} - ${r.error}`)
          .join('\n');
        addSystemMessage(`❌ Failed Commands:\n${failedCommands}`);
      }

      toast({
        title: 'Script Executed',
        description: `${result.successCount}/${result.totalCommands} commands completed successfully.`,
      });
    } catch (error) {
      console.error('Script upload error:', error);
      toast({
        title: "Script Upload Failed",
        description: error instanceof Error ? error.message : 'Unknown error occurred',
        variant: "destructive",
      });
    } finally {
      setIsUploadingScript(false);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Set filename
    setScriptFileName(file.name);
    
    // Read file content
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      setScriptContent(content);
    };
    reader.readAsText(file);
  };

  const exampleScript = allowTokenCreation
    ? `# Example Vaiu Script
# Lines starting with # are comments

# Check balances
balance

# Create some tokens
create token for $10
create token for $25 from my visa card
create invoice for $100

# Check history
transactions last 7 days

# Show balances again
balance USD
balance EUR`
    : `# Example Vaiu Script
# Lines starting with # are comments

# Check balances
balance

# Inspect a token (do not create tokens here)
token info ABC123

# Check history
transactions last 7 days`;

  const renderMessage = (message: Message) => {
    const isUser = message.type === 'user';
    const IconComponent = isUser ? User : Bot;
    
    return (
      <div key={message.id} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
        <div className={`flex max-w-[80%] ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
          <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
            isUser 
              ? 'bg-blue-500 text-white ml-2' 
              : 'bg-gray-200 text-gray-600 mr-2'
          }`}>
            <IconComponent size={16} />
          </div>
          <div className={`rounded-lg px-3 py-2 ${
            isUser 
              ? 'bg-blue-500 text-white' 
              : message.isProcessing 
                ? 'bg-yellow-50 text-yellow-800 border border-yellow-200'
                : 'bg-gray-100 text-gray-800'
          }`}>
            <div className="text-sm whitespace-pre-wrap">{message.text}</div>
            {message.executionTime && (
              <div className="text-xs opacity-60 mt-1 flex items-center">
                <Clock className="w-3 h-3 mr-1" />
                {message.executionTime}ms
              </div>
            )}
            {message.isProcessing && (
              <div className="text-xs opacity-60 mt-1 flex items-center">
                <div className="w-3 h-3 border-2 border-yellow-600 border-t-transparent rounded-full animate-spin mr-1" />
                Processing...
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen page-background">
      <div className="max-w-4xl mx-auto p-4">
        {/* Header */}
        <Card className="mb-4">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center space-x-2">
                <Terminal className="w-5 h-5 text-blue-600" />
                <span>Vaiu Shell</span>
                <Badge variant="secondary" className="ml-2">
                  <Sparkles className="w-3 h-3 mr-1" />
                  App API
                </Badge>
              </CardTitle>
              <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/')}
                  >
                    <Home className="w-4 h-4 mr-1" />
                    Home
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(-1)}
                  >
                    <ArrowLeft className="w-4 h-4 mr-1" />
                    Back
                  </Button>
                </div>
              </div>
          </CardHeader>
        </Card>

        {/* Chat Area */}
        <Card className="mb-4">
          <CardContent className="p-0">
            {/* Messages */}
            <div className="h-96 overflow-y-auto p-4 space-y-4">
              {messages.map(renderMessage)}
              <div ref={messagesEndRef} />
            </div>
            
            {/* Input */}
            <div className="border-t p-4">
              <div className="relative">
                <form onSubmit={handleSubmit} className="flex space-x-2">
                  <Input
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type your command... (e.g., 'create $50 token', 'redeem ABC123', 'show balances')"
                    disabled={isProcessing}
                    className="flex-1"
                  />
                  <Button 
                    type="submit" 
                    disabled={isProcessing || !input.trim()}
                    size="sm"
                  >
                    {isProcessing ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </form>
                
                {/* Suggestions dropdown */}
                {suggestions.length > 0 && (
                  <div className="absolute top-full left-0 right-12 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-10 max-h-48 overflow-y-auto">
                    {suggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        className={`px-3 py-2 text-sm cursor-pointer font-mono ${
                          index === selectedSuggestion
                            ? 'bg-blue-500 text-white'
                            : 'hover:bg-gray-100'
                        }`}
                        onClick={() => {
                          setInput(suggestion);
                          setSuggestions([]);
                          setSelectedSuggestion(-1);
                          inputRef.current?.focus();
                        }}
                      >
                        {suggestion}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="mt-2 text-xs text-gray-500 flex items-center justify-between">
                <span>
                  {commandHistory.length > 0 && `Commands: ${commandHistory.length} | `}
                  Tab for suggestions • ↑↓ for history{suggestions.length > 0 && ' • Esc to close'}
                </span>
                {suggestions.length > 0 && (
                  <span className="text-blue-600">
                    {suggestions.length} suggestion{suggestions.length > 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center justify-between">
              <span>Quick Actions</span>
              <div className="flex items-center space-x-2">
                <Dialog open={scriptDialogOpen} onOpenChange={setScriptDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Terminal className="w-4 h-4 mr-1" />
                      Script Upload
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>Upload and Execute Script</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Script File Name (optional)
                        </label>
                        <Input
                          value={scriptFileName}
                          onChange={(e) => setScriptFileName(e.target.value)}
                          placeholder="script.vaiu"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Script Content
                        </label>
                        <Textarea
                          value={scriptContent}
                          onChange={(e) => setScriptContent(e.target.value)}
                          placeholder="Enter your script commands here..."
                          className="min-h-[200px] font-mono text-sm"
                        />
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setScriptContent(exampleScript)}
                          disabled={isUploadingScript}
                        >
                          Load Example
                        </Button>
                        <div className="flex-1">
                          <input
                            type="file"
                            accept=".vaiu,.txt"
                            onChange={handleFileUpload}
                            className="hidden"
                            id="script-file-input"
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => document.getElementById('script-file-input')?.click()}
                            disabled={isUploadingScript}
                          >
                            Upload File
                          </Button>
                        </div>
                      </div>
                      
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-sm text-gray-600 mb-2">
                          <strong>Supported Commands:</strong>
                        </p>
                        <div className="text-xs text-gray-500 space-y-1">
                          <div>• balance, balance USD, balance EUR</div>
                          {allowTokenCreation && (
                            <div>• create token for $10, create invoice for $50</div>
                          )}
                          <div>• redeem ABC123</div>
                          <div>• transactions, transactions last 7 days</div>
                          <div>• # Comments (lines starting with #)</div>
                        </div>
                      </div>
                      
                      <div className="flex justify-end space-x-2">
                        <Button
                          variant="outline"
                          onClick={() => setScriptDialogOpen(false)}
                          disabled={isUploadingScript}
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleScriptUpload}
                          disabled={isUploadingScript || !scriptContent.trim()}
                        >
                          {isUploadingScript ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                              Executing...
                            </>
                          ) : (
                            'Execute Script'
                          )}
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setInput('show balances')}
                disabled={isProcessing}
              >
                <Wallet className="w-4 h-4 mr-1" />
                Show Balances
              </Button>
              {allowTokenCreation && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setInput('create $10 token')}
                disabled={isProcessing}
              >
                <QrCode className="w-4 h-4 mr-1" />
                Create $10 Token
              </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setInput('help')}
                disabled={isProcessing}
              >
                Help
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setInput('balance USD')}
                disabled={isProcessing}
              >
                <Wallet className="w-4 h-4 mr-1" />
                Balance USD
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VaiuShell;
